/**
 * Search component
 *
 * Handles advanced search redirection and context validation.
 * If no route parameters are present but context is available,
 * the user is redirected to the appropriate home route.
 *
 * It also initializes and stores time ranges in localStorage based on the scenario,
 * and allows selection of a search dimension which affects the redirection URL.
 */

import React, { FC, useState } from "react";
import cx from "classnames";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@foris/avocado-suite";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import ContextSearch from "@common/layout/components/context-search";

import css from "./search.module.scss";
import { resetAdvancedSearchStore } from "@context/useAdvancedSearchStore";

const Search: FC = () => {
  // Holds the selected dimension value coming from ContextSearch
  const [valueSelect, setValueSelect] = useState("");
  const [showOptions, setShowOptions] = useState(false);

  // Translation hook
  const { t } = useTranslation();

  // React Router history for programmatic navigation
  const history = useHistory();

  // Navigation URLs used throughout the app
  const { advancedSearchUrl, advancedSearchWithValueSelectUrl } = useNavigationUrl();

  // Updates the selected value from ContextSearch
  // Special case: converts "league" to "link"
  const getDimension = (value: string) => {
    if (value === "league") {
      setValueSelect("link");
    } else {
      setValueSelect(value);
    }
  };

  // Selects the appropriate advanced search URL based on selected value
  const routeAdvSearch = valueSelect
    ? advancedSearchWithValueSelectUrl(valueSelect)
    : advancedSearchUrl();

  return (
    <div className={cx(css.search, showOptions && css.search__showOptions)}>
      {/* Search input that sends selected dimension back to parent */}
      <ContextSearch
        className={css.search_contextSearch}
        sendDimension={getDimension}
        onShowOptions={setShowOptions}
      />

      {/* Button to navigate to the advanced search page */}
      <Button
        className={css.search_button}
        variant="ghost"
        rightIcon="chevron-right"
        onClick={() => {
          resetAdvancedSearchStore();
          history.push(routeAdvSearch);
        }}
      >
        {t("home.action-cards.editing-schedules.search.btn-advanced-search")}
      </Button>
    </div>
  );
};

export default Search;
